import {Box, Button, MenuItem, Stack, TextField} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {btnSx, FilterChips, Page, PageHeader, Panel, SkeletonRows} from "../../../../components/kit";
import {useApiManager} from "../../../../components/utils/api";
import {
	BudgetRestControllerApiFactory,
	ManagerStats,
	Overview,
	OverviewGranularityEnum,
	OverviewRestControllerApiFactory,
	OverviewSettings,
} from "arteco-api-client-ts";
import React, {useEffect, useState} from "react";
import {BillHomePage} from "./bill_home";
import {ResultKpis, TodayKpis} from "./kpis";
import {Attention} from "./attention";
import {BusinessLines} from "./lines";
import {SettingsDialog} from "./settings_dialog";
import {EmpProductivityBudgets} from "../../../employee/productivity/emp_productivity_budgets";

const GRANULARITIES = [
	{label: "Mes", value: OverviewGranularityEnum.MONTH},
	{label: "Trimestre", value: OverviewGranularityEnum.QUARTER},
	{label: "Año", value: OverviewGranularityEnum.YEAR},
];

/**
 * Visión general (#14497): resultado de un periodo cerrado con objetivo y semáforo, situación a hoy, lo que requiere
 * atención, la rentabilidad de cada línea de negocio y los presupuestos.
 */
export function OverviewPage() {
	const apiManager = useApiManager();
	const overviewCtrl = apiManager.factory(OverviewRestControllerApiFactory);
	const budgetCtrl = apiManager.factory(BudgetRestControllerApiFactory);
	const [granularity, setGranularity] = useState<OverviewGranularityEnum>(OverviewGranularityEnum.QUARTER);
	const [period, setPeriod] = useState<{ year: number, index: number } | undefined>(undefined);
	const [overview, setOverview] = useState<Overview | undefined>(undefined);
	const [productivity, setProductivity] = useState<ManagerStats | undefined>(undefined);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [trigger, setTrigger] = useState(0);

	useEffect(() => {
		apiManager.execute(budgetCtrl.managerStatsBudget(undefined)).then(setProductivity);
	}, []);

	useEffect(() => {
		setOverview(undefined);
		apiManager.execute(overviewCtrl.overview(granularity, period?.year, period?.index)).then(setOverview);
	}, [granularity, period, trigger]);

	const changeGranularity = (g: OverviewGranularityEnum) => {
		setPeriod(undefined);
		setGranularity(g);
	};

	const saveSettings = (s: OverviewSettings) =>
		apiManager.execute(overviewCtrl.saveSettings(s)).then(() => setTrigger(t => t + 1));

	const options = overview?.options || [];
	const selected = overview ? `${new Date(overview.current!.from + "T00:00:00").getFullYear()}-${indexOf(overview)}` : "";

	return (
		<Page>
			<PageHeader title="Visión general"
						subtitle={overview
							? `${overview.current!.label} · meses cerrados, con la factura en el mes de su servicio. La situación a hoy va aparte.`
							: "Meses cerrados, con la factura en el mes de su servicio."}
						actions={
							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
								<FilterChips options={GRANULARITIES} value={granularity} onChange={changeGranularity}/>
								<TextField select size="small" value={options.length ? selected : ""} sx={{minWidth: 170}}
										   onChange={e => {
											   const [y, i] = e.target.value.split("-").map(Number);
											   setPeriod({year: y, index: i});
										   }}
										   inputProps={{"aria-label": "Periodo"}}>
									{options.map(op => <MenuItem key={`${op.year}-${op.index}`} value={`${op.year}-${op.index}`}>{op.label}</MenuItem>)}
								</TextField>
								<Button variant="outlined" startIcon={<TuneRoundedIcon/>} sx={btnSx} disabled={!overview}
										onClick={() => setSettingsOpen(true)}>Objetivos</Button>
							</Stack>
						}/>
			{!overview
				? <SkeletonRows rows={6} height={120}/>
				: <Stack spacing={3}>
					<ResultKpis o={overview}/>
					<TodayKpis o={overview}/>
					<Attention o={overview} stats={productivity}/>
					<BusinessLines o={overview}/>
					{productivity
						? <EmpProductivityBudgets stats={productivity} withFilters/>
						// Lee las horas de Redmine en vivo: tras un despliegue tarda medio minuto en llegar.
						: <Panel title="Presupuestos" subtitle="Cargando las horas de Redmine; los excesos de horas se añaden también a «Qué requiere atención»."><SkeletonRows rows={4}/></Panel>}
					<BillHomePage/>
					<Box/>
				</Stack>}
			{overview?.settings &&
				<SettingsDialog open={settingsOpen} value={overview.settings} onClose={() => setSettingsOpen(false)} onSave={saveSettings}/>}
		</Page>
	);
}

/** El índice del periodo elegido (mes, trimestre o 1), para el desplegable. */
const indexOf = (o: Overview) => {
	const m = new Date(o.current!.from + "T00:00:00").getMonth() + 1;
	return o.granularity === "MONTH" ? m : o.granularity === "QUARTER" ? Math.floor((m - 1) / 3) + 1 : 1;
};
